import React, { useRef, useState } from 'react';
import { Upload, Link2, X, ExternalLink, File } from 'lucide-react';
import { Button } from '../../index';

/**
 * EvidenceUploader — supports both file attachments and external
 * URL links as attestation evidence. Drag-and-drop on the dropzone;
 * paste-or-type for URLs.
 *
 * The component is pure presentational — caller controls the list of
 * evidence items and the upload/delete handlers. We surface the
 * three common operations (add file / add URL / delete) and the
 * caller wires them to the appropriate API helpers.
 *
 * Used by AttestationsSlideout. Phase B / G321.
 *
 * @param {Object} props
 * @param {Array}  props.evidence       — { id, filename?, url?, addedAt }
 * @param {Function} props.onUploadFile — async (File) => void
 * @param {Function} props.onAddUrl     — async (url) => void
 * @param {Function} props.onDelete     — async (evidenceId) => void
 * @param {boolean} [props.disabled]
 */
export function EvidenceUploader({ evidence = [], onUploadFile, onAddUrl, onDelete, disabled = false }) {
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [busy, setBusy] = useState(null);  // 'upload' | 'url' | null

  const handleFiles = async (files) => {
    if (!onUploadFile || disabled) return;
    setBusy('upload');
    try {
      for (const f of Array.from(files)) {
        await onUploadFile(f);
      }
    } finally { setBusy(null); }
  };

  const handleAddUrl = async () => {
    const url = urlInput.trim();
    if (!url || !onAddUrl || disabled) return;
    setBusy('url');
    try {
      await onAddUrl(url);
      setUrlInput('');
    } finally { setBusy(null); }
  };

  return (
    <div>
      {/* Existing evidence list */}
      {evidence.length > 0 && (
        <ul className="space-y-1.5 mb-3 m-0 p-0 list-none">
          {evidence.map((e) => (
            <li key={e.id || e.url || e.filename}
                className="flex items-center gap-2 bg-v2-surface border border-v2-border-subtle rounded-v2-sm px-2.5 py-1.5">
              {e.url ? <Link2 size={13} className="text-v2-action-strong shrink-0" />
                     : <File size={13} className="text-v2-action-strong shrink-0" />}
              {e.url ? (
                <a href={e.url} target="_blank" rel="noopener noreferrer"
                   className="text-[12.5px] text-v2-action-strong hover:underline truncate flex-1 inline-flex items-center gap-1">
                  {e.url}
                  <ExternalLink size={10} />
                </a>
              ) : (
                <span className="text-[12.5px] text-v2-ink truncate flex-1">{e.filename || e.id}</span>
              )}
              <span className="text-[10.5px] text-v2-ink-muted shrink-0">
                {e.addedAt ? new Date(e.addedAt).toLocaleDateString() : ''}
              </span>
              {onDelete && !disabled && (
                <button onClick={() => onDelete(e.id)} aria-label="Delete evidence"
                        className="text-v2-ink-muted hover:text-v2-danger shrink-0">
                  <X size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Dropzone */}
      {onUploadFile && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (disabled) return;
            handleFiles(e.dataTransfer.files);
          }}
          className={`border-2 border-dashed rounded-v2-md p-4 text-center text-[12.5px] transition-colors ${
            disabled ? 'border-v2-border-subtle bg-v2-surface text-v2-ink-muted cursor-not-allowed'
            : dragging ? 'border-v2-action bg-v2-action-soft text-v2-action-strong'
                       : 'border-v2-border-subtle bg-v2-surface hover:border-v2-action/40'
          }`}
        >
          <Upload size={20} className="mx-auto mb-1 text-v2-ink-muted" />
          <p className="m-0 mb-2">
            Drag &amp; drop files here or{' '}
            <button onClick={() => fileInputRef.current?.click()}
                    className="text-v2-action-strong hover:underline font-semibold"
                    disabled={disabled}>
              browse
            </button>
          </p>
          <p className="text-[10.5px] text-v2-ink-muted m-0">
            PDF, PNG, JPG, DOCX up to 25 MB. Files are encrypted at rest in the audit vault.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={disabled || busy === 'upload'}
          />
        </div>
      )}

      {/* URL input */}
      {onAddUrl && (
        <div className="mt-2 flex items-center gap-2">
          <Link2 size={13} className="text-v2-ink-muted shrink-0" />
          <input
            type="url"
            placeholder="https://… (paste a link to existing evidence)"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
            disabled={disabled || busy === 'url'}
            className="flex-1 border border-v2-border-subtle rounded-v2-sm px-2.5 py-1.5 text-[12.5px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none disabled:bg-v2-muted"
          />
          <Button variant="secondary" size="sm" onClick={handleAddUrl} disabled={disabled || !urlInput.trim() || busy === 'url'}>
            {busy === 'url' ? 'Adding…' : 'Add'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default EvidenceUploader;
